import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRightLeft,
  BadgeCheck,
  Banknote,
  Briefcase,
  Building2,
  CalendarDays,
  Clock3,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  StickyNote,
  TrendingUp,
  UserCheck,
} from 'lucide-react'
import { apiJson } from '../api'
import { canManageEmployees } from '../auth'
import Modal from '../components/Modal'

interface EmployeeProfile {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  employmentStatus: string
  joinDate: string
  exitDate?: string | null
  positionId?: string | null
  orgUnitId?: string | null
  orgUnitName?: string | null
  reportsToId?: string | null
  manager?: { id: string; name: string; employeeCode: string } | null
  username?: string | null
  lastPromotionDate?: string | null
}

interface EmployeeEvent {
  id: string
  eventType: 'hired' | 'promoted' | 'transferred' | 'status_change' | 'note'
  eventDate: string
  fromValue?: string | null
  toValue?: string | null
  note?: string | null
  createdBy?: string | null
}

interface WorkSummary {
  currency: string
  latestPayslip: {
    period: string
    baseSalary: number
    allowances: number
    deductions: number
    netPay: number
    status: string
    currency: string
  } | null
  attendanceMonth: { daysPresent: number; workedMinutes: number; timezone: string }
  leave: {
    year: number
    pendingRequests: number
    balances: { leaveType: string; entitled: number | null; used: number; remaining: number | null }[]
  } | null
}

const statusMeta: Record<string, { label: string; cls: string; dot: string }> = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', dot: 'bg-emerald-500' },
  hired: { label: 'Hired', cls: 'bg-teal-50 text-teal-700 ring-teal-100', dot: 'bg-teal-500' },
  promoted: { label: 'Promoted', cls: 'bg-blue-50 text-blue-700 ring-blue-100', dot: 'bg-blue-500' },
  transferred: { label: 'Transferred', cls: 'bg-sky-50 text-sky-700 ring-sky-100', dot: 'bg-sky-500' },
  applicant: { label: 'Applicant', cls: 'bg-amber-50 text-amber-700 ring-amber-100', dot: 'bg-amber-500' },
  exited: { label: 'Exited', cls: 'bg-slate-100 text-slate-500 ring-slate-200', dot: 'bg-slate-400' },
}

const avatarTints = [
  'from-blue-500 to-brand-600',
  'from-brand-600 to-brand-700',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
]

function avatarTint(name: string) {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) | 0
  return avatarTints[Math.abs(hash) % avatarTints.length]
}

const eventMeta: Record<string, { label: string; cls: string; icon: typeof Briefcase }> = {
  hired: { label: 'Hired', cls: 'bg-teal-50 text-teal-600 ring-teal-100', icon: Briefcase },
  promoted: { label: 'Promoted', cls: 'bg-blue-50 text-blue-600 ring-blue-100', icon: TrendingUp },
  transferred: { label: 'Transferred', cls: 'bg-sky-50 text-sky-600 ring-sky-100', icon: ArrowRightLeft },
  status_change: { label: 'Status change', cls: 'bg-amber-50 text-amber-600 ring-amber-100', icon: RefreshCw },
  note: { label: 'Note', cls: 'bg-slate-100 text-slate-500 ring-slate-200', icon: StickyNote },
}

const EVENT_TYPES = ['promoted', 'transferred', 'note'] as const

function fmtDate(iso?: string | null) {
  return iso ? iso.slice(0, 10) : '—'
}

function tenure(joinDate: string, exitDate?: string | null) {
  const start = new Date(joinDate)
  const end = exitDate ? new Date(exitDate) : new Date()
  if (Number.isNaN(start.getTime())) return '—'
  const months = Math.max(0, Math.round((end.getTime() - start.getTime()) / (30.44 * 86400000)))
  const years = Math.floor(months / 12)
  const rem = months % 12
  if (years === 0) return `${rem} mo`
  return rem === 0 ? `${years} yr` : `${years} yr ${rem} mo`
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function EmployeeProfile() {
  const { id } = useParams<{ id: string }>()
  const allowManage = canManageEmployees()
  const [profile, setProfile] = useState<EmployeeProfile | null>(null)
  const [history, setHistory] = useState<EmployeeEvent[]>([])
  const [summary, setSummary] = useState<WorkSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [eventOpen, setEventOpen] = useState(false)
  const [eventForm, setEventForm] = useState({ eventType: 'promoted' as string, eventDate: new Date().toISOString().slice(0, 10), note: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const fetchAll = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([
        apiJson<EmployeeProfile>(`/employees/${id}/profile`),
        apiJson<EmployeeEvent[]>(`/employees/${id}/history`),
      ])
      setProfile(p)
      setHistory(h)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load employee profile')
    } finally {
      setLoading(false)
    }
    // Work summary is admin/hr-only (contains salary data); best-effort
    if (canManageEmployees()) {
      try {
        setSummary(await apiJson<WorkSummary>(`/employees/${id}/work-summary`))
      } catch {
        setSummary(null)
      }
    }
  }, [id])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : ''
  const initials = profile ? `${profile.firstName[0] ?? ''}${profile.lastName[0] ?? ''}`.toUpperCase() : ''
  const st = statusMeta[profile?.employmentStatus ?? ''] ?? statusMeta.active

  const keyDates = useMemo(() => {
    if (!profile) return []
    return [
      { icon: Briefcase, label: 'Joined', value: fmtDate(profile.joinDate) },
      { icon: TrendingUp, label: 'Last promotion', value: profile.lastPromotionDate ? fmtDate(profile.lastPromotionDate) : '—' },
      { icon: Clock3, label: 'Tenure', value: tenure(profile.joinDate, profile.exitDate) },
      ...(profile.exitDate ? [{ icon: CalendarDays, label: 'Exit date', value: fmtDate(profile.exitDate) }] : []),
    ]
  }, [profile])

  const saveEvent = async () => {
    setSaving(true)
    setSaveError('')
    try {
      await apiJson(`/employees/${id}/history`, {
        method: 'POST',
        body: JSON.stringify({
          eventType: eventForm.eventType,
          eventDate: eventForm.eventDate,
          ...(eventForm.note.trim() ? { note: eventForm.note.trim() } : {}),
        }),
      })
      setEventOpen(false)
      setEventForm({ eventType: 'promoted', eventDate: new Date().toISOString().slice(0, 10), note: '' })
      await fetchAll()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to add event')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }
  if (loadError || !profile) {
    return (
      <div className="space-y-4">
        <Link to="/employees" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700">
          <ArrowLeft size={14} /> Back to employees
        </Link>
        <div className="flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
          <AlertCircle size={16} /> {loadError || 'Employee not found'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <Link to="/employees" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700">
        <ArrowLeft size={14} /> Back to employees
      </Link>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-7 text-white shadow-lg">
        <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className={`flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br text-2xl font-extrabold text-white ring-2 ring-white/30 ${avatarTint(fullName)}`}>
              {initials}
            </div>
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight">{fullName}</h2>
              <p className="mt-0.5 text-sm text-white/70">
                {profile.employeeCode}
                {profile.username && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-xs">SSO: {profile.username}</span>}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${st.cls}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                  {st.label}
                </span>
                {profile.orgUnitName && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium ring-1 ring-white/20">
                    <Building2 size={11} /> {profile.orgUnitName}
                  </span>
                )}
                {profile.manager && (
                  <Link
                    to={`/employees/${profile.manager.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium ring-1 ring-white/20 transition-colors hover:bg-white/20"
                  >
                    <UserCheck size={11} /> Reports to {profile.manager.name}
                  </Link>
                )}
              </div>
            </div>
          </div>
          {allowManage && (
            <button
              onClick={() => { setSaveError(''); setEventOpen(true) }}
              className="inline-flex items-center gap-2 self-start rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 shadow-sm transition-all hover:bg-brand-50 sm:self-center"
            >
              <Plus size={15} /> Add history event
            </button>
          )}
        </div>
      </div>

      {/* Key dates + contact */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {keyDates.map((d) => (
          <div key={d.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <d.icon size={14} />
              <p className="text-xs font-semibold uppercase tracking-wider">{d.label}</p>
            </div>
            <p className="mt-1.5 text-lg font-extrabold tracking-tight tabular-nums">{d.value}</p>
          </div>
        ))}
      </div>

      {/* Work summary (admin/hr) */}
      {allowManage && summary && (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Latest payslip */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Banknote size={14} />
              <p className="text-xs font-semibold uppercase tracking-wider">Latest payslip</p>
            </div>
            {summary.latestPayslip ? (
              <>
                <p className="mt-2 text-xl font-extrabold tracking-tight tabular-nums">
                  {summary.latestPayslip.netPay.toLocaleString('en-US')} {summary.latestPayslip.currency}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {summary.latestPayslip.period} · base {summary.latestPayslip.baseSalary.toLocaleString('en-US')}
                  {' '}+{summary.latestPayslip.allowances.toLocaleString('en-US')} −{summary.latestPayslip.deductions.toLocaleString('en-US')}
                </p>
                <span className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600 ring-1 ring-slate-200">
                  {summary.latestPayslip.status}
                </span>
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-400">No payslips yet</p>
            )}
          </div>

          {/* Attendance this month */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Clock3 size={14} />
              <p className="text-xs font-semibold uppercase tracking-wider">Attendance this month</p>
            </div>
            <p className="mt-2 text-xl font-extrabold tracking-tight tabular-nums">
              {summary.attendanceMonth.daysPresent} day{summary.attendanceMonth.daysPresent === 1 ? '' : 's'} present
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              {Math.floor(summary.attendanceMonth.workedMinutes / 60)}h {summary.attendanceMonth.workedMinutes % 60}m worked · {summary.attendanceMonth.timezone}
            </p>
          </div>

          {/* Leave balances */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <CalendarDays size={14} />
              <p className="text-xs font-semibold uppercase tracking-wider">Leave {summary.leave ? summary.leave.year : ''}</p>
            </div>
            {summary.leave ? (
              <>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {summary.leave.balances.map((b) => (
                    <span key={b.leaveType} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600">
                      {b.leaveType}: {b.remaining === null ? `${b.used} taken` : `${b.remaining} left`}
                    </span>
                  ))}
                </div>
                {summary.leave.pendingRequests > 0 && (
                  <p className="mt-2 text-xs font-semibold text-amber-600">
                    {summary.leave.pendingRequests} pending request{summary.leave.pendingRequests === 1 ? '' : 's'}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-400">Not linked to an SSO account</p>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Contact */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-bold tracking-tight">Contact</h3>
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-slate-100 p-2 text-slate-500"><Mail size={14} /></div>
              <span className="truncate text-sm text-slate-700">{profile.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-slate-100 p-2 text-slate-500"><Phone size={14} /></div>
              <span className="text-sm text-slate-700">{profile.phone || '—'}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-slate-100 p-2 text-slate-500"><BadgeCheck size={14} /></div>
              <span className="text-sm text-slate-700">Joined {fmtDate(profile.joinDate)}</span>
            </div>
          </div>
        </div>

        {/* History timeline */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h3 className="text-sm font-bold tracking-tight">Employment History</h3>
          {history.length === 0 ? (
            <p className="py-8 text-center text-xs text-slate-400">No history entries yet.</p>
          ) : (
            <ol className="relative mt-4 space-y-4 border-l border-slate-100 pl-5">
              {history.map((ev) => {
                const meta = eventMeta[ev.eventType] ?? eventMeta.note
                const Icon = meta.icon
                return (
                  <li key={ev.id} className="relative">
                    <span className={`absolute -left-[27px] flex h-5 w-5 items-center justify-center rounded-full ring-1 ${meta.cls}`}>
                      <Icon size={11} />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-semibold capitalize">{meta.label}</span>
                      <span className="text-xs tabular-nums text-slate-400">{fmtDate(ev.eventDate)}</span>
                      {ev.createdBy && <span className="text-[11px] text-slate-300">by {ev.createdBy}</span>}
                    </div>
                    {(ev.fromValue || ev.toValue) && (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {[ev.fromValue, ev.toValue].filter(Boolean).join(' → ')}
                      </p>
                    )}
                    {ev.note && <p className="mt-0.5 text-xs text-slate-400">{ev.note}</p>}
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </div>

      {/* Add event modal */}
      {eventOpen && allowManage && (
        <Modal
          title={`Add history event — ${fullName}`}
          subtitle="A promotion also flips the employment status to Promoted"
          onClose={() => setEventOpen(false)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Event type</label>
                <select value={eventForm.eventType} onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })} className={inputCls}>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">{eventMeta[t].label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Effective date *</label>
                <input required type="date" value={eventForm.eventDate} onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Note</label>
              <textarea
                value={eventForm.note}
                onChange={(e) => setEventForm({ ...eventForm, note: e.target.value })}
                rows={3}
                maxLength={500}
                placeholder={eventForm.eventType === 'promoted' ? 'e.g. Junior → Senior Developer' : 'Optional context…'}
                className={`${inputCls} resize-none`}
              />
            </div>

            {saveError && (
              <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0" /> {saveError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                onClick={() => setEventOpen(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEvent}
                disabled={saving || !eventForm.eventDate}
                className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Add event'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
